/**
 * City page — spec §5.4.
 *
 * Route: `/city/:slug`. One API call (`GET /api/v1/cities/{slug}/summary`)
 * supplies the contract stage, drive window, notes, every ticket touching the
 * city grouped by status, the hardware assigned there, and the blockers — which
 * is what drives the "what's blocking this city" callout. The dependency graph
 * is embedded below, scoped to the city.
 */
import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { count } from '../../lib/format'

import DependencyGraph from '../../components/graph/DependencyGraph.jsx'
import '../../components/graph/graph.css'
import { AssetStatusChip, Card, Placeholder, StatusBar, TicketRow } from './parts.jsx'
import {
  CLOSED_STATUSES,
  STATUSES,
  STATUS_META,
  apiGet,
  cls,
  fmtDate,
  fmtRange,
  qs,
  useAsync,
} from './shared.js'

/** Statuses that start expanded — closed work is collapsed by default. */
const OPEN_BY_DEFAULT = STATUSES.filter((s) => !CLOSED_STATUSES.has(s))

export default function CityPage() {
  const { slug } = useParams()
  const [params, setParams] = useSearchParams()
  const [collapsed, setCollapsed] = useState(
    () => new Set(STATUSES.filter((s) => !OPEN_BY_DEFAULT.includes(s))),
  )
  const openOnly = params.get('open') !== '0'

  const summary = useAsync(() => apiGet(`/cities/${slug}/summary`), [slug])
  const graph = useAsync(
    () => apiGet(`/graph${qs({ city: slug, open_only: openOnly })}`),
    [slug, openOnly],
  )

  const data = summary.data
  const city = data?.city
  const groups = useMemo(
    () => (data ? data.groups.filter((g) => g.tickets.length) : []),
    [data],
  )

  const toggle = (status) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(status)) next.delete(status)
      else next.add(status)
      return next
    })

  if (summary.loading || summary.error || !data) {
    return (
      <div className="pm-page">
        <Placeholder loading={summary.loading} error={summary.error} />
      </div>
    )
  }

  return (
    <div className="pm-page">
      <header className="pm-page-head">
        <div>
          <div className="pm-crumbs">
            <Link to="/cities">Cities</Link>
            <span>/</span>
            <span>{city.slug}</span>
          </div>
          <h1 className="pm-title">
            {city.name}
            {city.state ? <span className="pm-sub"> · {city.state}</span> : null}
          </h1>
          <p className="pm-sub">
            {data.open_count} open of{' '}
            {count(
              Object.values(data.counts_by_status).reduce((a, b) => a + b, 0),
              'ticket',
            )}{' '}
            · {count(data.assets.length, 'asset')} assigned
          </p>
        </div>
        <div className="pm-toolbar">
          <Link className="btn" to={`/list${qs({ city: city.slug })}`}>
            Open in list
          </Link>
          <Link className="btn" to={`/${qs({ city: city.slug })}`}>
            Open board
          </Link>
          <Link className="btn" to={`/graph${qs({ city: city.slug })}`}>
            Full graph
          </Link>
        </div>
      </header>

      <div className="pm-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {data.blocking.length ? (
            <Card
              title={`Blocking ${city.name} — ${data.blocking.length} open ticket${
                data.blocking.length === 1 ? '' : 's'
              }`}
              bodyClass="pm-tickets"
            >
              {data.blocking.map((t) => (
                <TicketRow key={t.id} ticket={t} showStatus showCities />
              ))}
            </Card>
          ) : (
            <Card title={`Blocking ${city.name}`}>
              <div className="pm-empty">
                Nothing open is blocking this city&apos;s work right now.
              </div>
            </Card>
          )}

          <Card title="Tickets by status" bodyClass="">
            {groups.length === 0 ? (
              <div className="pm-empty">No tickets reference this city yet.</div>
            ) : (
              groups.map((group) => {
                const isCollapsed = collapsed.has(group.status)
                const meta = STATUS_META[group.status] || { label: group.status }
                return (
                  <div key={group.status}>
                    <button
                      type="button"
                      className="pm-group-head"
                      onClick={() => toggle(group.status)}
                      aria-expanded={!isCollapsed}
                    >
                      <span className="pm-dot" style={{ background: meta.color }} />
                      {meta.label}
                      <span className="pm-count">{group.tickets.length}</span>
                      <span style={{ marginLeft: 'auto' }}>{isCollapsed ? '▸' : '▾'}</span>
                    </button>
                    {isCollapsed ? null : (
                      <div className="pm-tickets">
                        {group.tickets.map((t) => (
                          <TicketRow key={t.id} ticket={t} />
                        ))}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </Card>

          <Card
            title="Dependency graph — scoped to this city"
            right={
              <label className="pm-check">
                <input
                  type="checkbox"
                  checked={openOnly}
                  onChange={(e) => {
                    const next = new URLSearchParams(params)
                    next.set('open', e.target.checked ? '1' : '0')
                    setParams(next, { replace: true })
                  }}
                />
                open only
              </label>
            }
            bodyClass=""
          >
            {graph.loading ? (
              <div className="pm-empty">Loading graph…</div>
            ) : graph.error ? (
              <div className="pm-card-body">
                <div className="pm-error">{String(graph.error.message || graph.error)}</div>
              </div>
            ) : (
              <DependencyGraph
                nodes={graph.data.nodes}
                edges={graph.data.edges}
                height={420}
              />
            )}
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card title="Engagement">
            <dl className="pm-meta">
              <dt>Contract stage</dt>
              <dd>{city.contract_stage || '—'}</dd>
              <dt>Drive window</dt>
              <dd>{fmtRange(city.drive_start, city.drive_end)}</dd>
              <dt>Key prefix</dt>
              <dd className="key">{city.key_prefix || '—'}</dd>
              <dt>Slug</dt>
              <dd className="key">{city.slug}</dd>
            </dl>
            <div style={{ marginTop: 12 }}>
              <StatusBar counts={data.counts_by_status} />
              <div
                className="pm-sub"
                style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}
              >
                {STATUSES.filter((s) => data.counts_by_status[s]).map((s) => (
                  <span key={s}>
                    <span
                      className="pm-dot"
                      style={{
                        background: STATUS_META[s].color,
                        display: 'inline-block',
                        marginRight: 4,
                      }}
                    />
                    {STATUS_META[s].label} {data.counts_by_status[s]}
                  </span>
                ))}
              </div>
            </div>
            {city.notes ? (
              <p style={{ marginTop: 12, fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{city.notes}</p>
            ) : null}
          </Card>

          <Card
            title={`Assets · ${data.assets.length}`}
            right={
              <Link className="key" to={`/assets${qs({ city: city.slug })}`}>
                registry →
              </Link>
            }
            bodyClass=""
          >
            {data.assets.length === 0 ? (
              <div className="pm-empty">No hardware assigned to this city.</div>
            ) : (
              <table className="pm-table">
                <tbody>
                  {data.assets.map((a) => (
                    <tr key={a.id}>
                      <td className="key">{a.kind}</td>
                      <td>
                        <Link to={`/assets${qs({ focus: a.id })}`} style={{ color: 'inherit' }}>
                          {a.model}
                        </Link>
                        {a.serial ? <div className="key">{a.serial}</div> : null}
                      </td>
                      <td>
                        <AssetStatusChip status={a.status} />
                      </td>
                      <td className="key">{a.location || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}

/**
 * Index of every city engagement — route `/cities`.
 * Each row links to its city page and shows contract stage + drive window.
 */
export function CityIndex() {
  const { data, loading, error } = useAsync(() => apiGet('/cities'), [])
  return (
    <div className="pm-page">
      <header className="pm-page-head">
        <div>
          <h1 className="pm-title">Cities</h1>
          <p className="pm-sub">Every engagement, its contract stage and drive window.</p>
        </div>
      </header>
      <Card bodyClass="">
        <Placeholder loading={loading} error={error} empty={!!data && !data.length}>
          No cities yet.
        </Placeholder>
        {data && data.length ? (
          <table className="pm-table">
            <thead>
              <tr>
                <th>City</th>
                <th>State</th>
                <th>Contract stage</th>
                <th>Drive window</th>
                <th>Prefix</th>
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/city/${c.slug}`} style={{ color: 'inherit', fontWeight: 600 }}>
                      {c.name}
                    </Link>
                    <div className="key">{c.slug}</div>
                  </td>
                  <td>{c.state || '—'}</td>
                  <td>
                    <span className={cls('chip')}>{c.contract_stage || 'unset'}</span>
                  </td>
                  <td className="key">{fmtRange(c.drive_start, c.drive_end)}</td>
                  <td className="key">{c.key_prefix || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Card>
      <p className="pm-sub">Dates shown in UTC ({fmtDate(new Date().toISOString())} today).</p>
    </div>
  )
}
